import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/shared/Button/Button';
import Loading from '../../../components/shared/Loading/Loading';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import {
  PanelContainer,
  PanelHeader,
  StatsRow,
  StatBox,
  SectionTabs,
  SectionTab,
  TabRow,
  Tab,
  EmpresaList,
  EmpresaCard,
  EmpresaMain,
  EmpresaMeta,
  DocList,
  CardActions,
  DataTable,
  EmptyState,
} from './AdminPanel.styles';
import AdminPromoters from '../Promoters/AdminPromoters';

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

const AdminPanel = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [section, setSection] = useState('empresas'); // empresas | actividades | reservas | promotores

  const [empresas, setEmpresas] = useState([]);
  const [empresaFilter, setEmpresaFilter] = useState('pending');
  const [activities, setActivities] = useState([]);
  const [activityFilter, setActivityFilter] = useState('active');
  const [reservations, setReservations] = useState([]);
  const [reservationFilter, setReservationFilter] = useState('all');

  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(null);

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('admin-token')}`,
  }), []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('admin-token');
    localStorage.removeItem('admin');
    navigate('/admin/login');
  }, [navigate]);

  const apiGet = useCallback(async (path) => {
    const res = await fetch(path, { headers: authHeaders() });
    if (res.status === 401) {
      handleLogout();
      return null;
    }
    return res.json();
  }, [authHeaders, handleLogout]);

  const loadStats = useCallback(async () => {
    const data = await apiGet('/api/admin/stats');
    if (data?.success) setStats(data.stats);
  }, [apiGet]);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      if (section === 'empresas') {
        const data = await apiGet(`/api/admin/empresas?status=${empresaFilter}`);
        if (data?.success) setEmpresas(data.empresas);
      } else if (section === 'actividades') {
        const data = await apiGet(`/api/admin/activities?status=${activityFilter}&limit=50`);
        if (data?.success) setActivities(data.activities);
      } else if (section === 'promotores') {
        // la sección de promotores carga sus propios datos (AdminPromoters)
      } else {
        const data = await apiGet(`/api/admin/reservations?status=${reservationFilter}&limit=50`);
        if (data?.success) setReservations(data.reservations);
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [section, empresaFilter, activityFilter, reservationFilter, apiGet]);

  useEffect(() => {
    const token = localStorage.getItem('admin-token');
    const adminData = localStorage.getItem('admin');

    if (!token || !adminData) {
      navigate('/admin/login');
      return;
    }

    setAdmin(JSON.parse(adminData));
    loadStats();
  }, [navigate, loadStats]);

  useEffect(() => {
    if (localStorage.getItem('admin-token')) {
      loadSection();
    }
  }, [loadSection]);

  const handleApprove = async (empresa) => {
    if (!window.confirm(`¿Aprobar a ${empresa.businessName}? Podrá publicar actividades de inmediato.`)) {
      return;
    }

    setActionInProgress(empresa.id);
    try {
      const response = await fetch(`/api/admin/empresas/${empresa.id}/approve`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const result = await response.json();

      if (result.success) {
        loadSection();
        loadStats();
      } else {
        alert(result.error || 'No se pudo aprobar la empresa');
      }
    } catch (error) {
      alert('Error de conexión');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = async (empresa) => {
    const reason = window.prompt(
      `Motivo del rechazo de ${empresa.businessName} (se enviará por email):`
    );
    if (reason === null) return;

    setActionInProgress(empresa.id);
    try {
      const response = await fetch(`/api/admin/empresas/${empresa.id}/reject`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const result = await response.json();

      if (result.success) {
        loadSection();
        loadStats();
      } else {
        alert(result.error || 'No se pudo rechazar la empresa');
      }
    } catch (error) {
      alert('Error de conexión');
    } finally {
      setActionInProgress(null);
    }
  };

  if (!admin) return null;

  return (
    <PanelContainer>
      <PanelHeader>
        <div className="brand">
          <a href="/" aria-label="BokerHub, ir al inicio"><img src="/assets/logos/logo-color.png" alt="BokerHub" /></a>
          <span className="admin-chip">Admin</span>
        </div>
        <div className="user">
          <span>{admin.name}</span>
          <Button variant="ghost" size="small" onClick={handleLogout}>
            Salir
          </Button>
        </div>
      </PanelHeader>

      <div className="content">
        {stats && (
          <StatsRow>
            <StatBox $accent="var(--warning)">
              <strong>{stats.pendingEmpresas}</strong>
              <span>Empresas pendientes</span>
            </StatBox>
            {typeof stats.pendingPromoters === 'number' && (
              <StatBox $accent="var(--warning)">
                <strong>{stats.pendingPromoters}</strong>
                <span>Promotores pendientes</span>
              </StatBox>
            )}
            <StatBox $accent="var(--success-dark)">
              <strong>{stats.verifiedEmpresas}</strong>
              <span>Empresas verificadas</span>
            </StatBox>
            <StatBox $accent="var(--primary)">
              <strong>{stats.activeActivities}</strong>
              <span>Actividades activas</span>
            </StatBox>
            <StatBox $accent="var(--text-muted)">
              <strong>{stats.closedActivities}</strong>
              <span>Actividades cerradas</span>
            </StatBox>
            <StatBox $accent="var(--primary-light)">
              <strong>{stats.totalReservations}</strong>
              <span>Reservas totales</span>
            </StatBox>
            <StatBox $accent="var(--danger)">
              <strong>{stats.cancelledReservations}</strong>
              <span>Reservas canceladas</span>
            </StatBox>
          </StatsRow>
        )}

        <SectionTabs>
          <SectionTab $active={section === 'empresas'} onClick={() => setSection('empresas')}>
            🏢 Empresas
          </SectionTab>
          <SectionTab $active={section === 'actividades'} onClick={() => setSection('actividades')}>
            🎯 Actividades
          </SectionTab>
          <SectionTab $active={section === 'reservas'} onClick={() => setSection('reservas')}>
            📋 Reservas
          </SectionTab>
          <SectionTab $active={section === 'promotores'} onClick={() => setSection('promotores')}>
            🤝 Promotores
          </SectionTab>
        </SectionTabs>

        {/* ─── PROMOTORES ─── */}
        {section === 'promotores' && (
          <AdminPromoters onUnauthorized={handleLogout} onChanged={loadStats} />
        )}

        {/* ─── EMPRESAS ─── */}
        {section === 'empresas' && (
          <>
            <TabRow>
              <Tab $active={empresaFilter === 'pending'} onClick={() => setEmpresaFilter('pending')}>
                Pendientes
              </Tab>
              <Tab $active={empresaFilter === 'verified'} onClick={() => setEmpresaFilter('verified')}>
                Verificadas
              </Tab>
              <Tab $active={empresaFilter === 'all'} onClick={() => setEmpresaFilter('all')}>
                Todas
              </Tab>
            </TabRow>

            {loading ? (
              <Loading text="Cargando empresas..." />
            ) : empresas.length === 0 ? (
              <EmptyState>
                <h3>
                  {empresaFilter === 'pending'
                    ? 'No hay empresas pendientes de verificación'
                    : 'No hay empresas en esta vista'}
                </h3>
                <p>Las nuevas solicitudes aparecerán aquí.</p>
              </EmptyState>
            ) : (
              <EmpresaList>
                {empresas.map(empresa => (
                  <EmpresaCard key={empresa.id}>
                    <EmpresaMain>
                      <div className="title-row">
                        <h3>{empresa.businessName}</h3>
                        <StatusBadge status={empresa.verified ? 'verified' : 'pending'}>
                          {empresa.verified ? 'Verificada' : 'Pendiente'}
                        </StatusBadge>
                      </div>
                      <EmpresaMeta>
                        <span>👤 {empresa.contactName}</span>
                        <span>✉️ {empresa.email}</span>
                        {empresa.phone && <span>📞 {empresa.phone}</span>}
                        <span>📍 {[empresa.city, empresa.state].filter(Boolean).join(', ') || 'Sin ubicación'}</span>
                        <span>🏷️ {empresa.businessType || 'Sin tipo'}</span>
                        <span>🎯 {empresa.activitiesCount} actividad(es)</span>
                        <span>🗓️ Registro: {fmtDate(empresa.createdAt)}</span>
                      </EmpresaMeta>

                      {empresa.description && <p className="description">{empresa.description}</p>}

                      <DocList>
                        <strong>Documentos:</strong>
                        {empresa.documents.length > 0 ? (
                          empresa.documents.map(doc => (
                            <a
                              key={doc.id}
                              href={doc.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              📄 {doc.type}
                            </a>
                          ))
                        ) : (
                          <span className="no-docs">Sin documentos subidos</span>
                        )}
                      </DocList>
                    </EmpresaMain>

                    <CardActions>
                      {!empresa.verified ? (
                        <>
                          <Button
                            variant="success"
                            size="small"
                            disabled={actionInProgress === empresa.id}
                            onClick={() => handleApprove(empresa)}
                          >
                            ✓ Aprobar
                          </Button>
                          <Button
                            variant="danger"
                            size="small"
                            disabled={actionInProgress === empresa.id}
                            onClick={() => handleReject(empresa)}
                          >
                            ✕ Rechazar
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="danger"
                          size="small"
                          disabled={actionInProgress === empresa.id}
                          onClick={() => handleReject(empresa)}
                        >
                          Revocar verificación
                        </Button>
                      )}
                    </CardActions>
                  </EmpresaCard>
                ))}
              </EmpresaList>
            )}
          </>
        )}

        {/* ─── ACTIVIDADES ─── */}
        {section === 'actividades' && (
          <>
            <TabRow>
              <Tab $active={activityFilter === 'active'} onClick={() => setActivityFilter('active')}>
                Activas
              </Tab>
              <Tab $active={activityFilter === 'closed'} onClick={() => setActivityFilter('closed')}>
                Cerradas
              </Tab>
              <Tab $active={activityFilter === 'draft'} onClick={() => setActivityFilter('draft')}>
                Borradores
              </Tab>
              <Tab $active={activityFilter === 'all'} onClick={() => setActivityFilter('all')}>
                Todas
              </Tab>
            </TabRow>

            {loading ? (
              <Loading text="Cargando actividades..." />
            ) : activities.length === 0 ? (
              <EmptyState>
                <h3>No hay actividades en esta vista</h3>
                <p>Cambia el filtro para ver otras.</p>
              </EmptyState>
            ) : (
              <DataTable>
                <table>
                  <thead>
                    <tr>
                      <th>Actividad</th>
                      <th>Empresa</th>
                      <th>Ubicación</th>
                      <th>Precio</th>
                      <th>Reservas</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map(a => (
                      <tr key={a.id}>
                        <td>
                          <strong>{a.title}</strong>
                          <div className="sub">{a.type}</div>
                        </td>
                        <td>
                          {a.empresaName || '—'}
                          {!a.empresaVerified && <div className="sub warn">sin verificar</div>}
                        </td>
                        <td>{a.location}</td>
                        <td>{fmtMoney(a.price)}</td>
                        <td>{a.totalReservations}</td>
                        <td><StatusBadge status={a.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DataTable>
            )}
          </>
        )}

        {/* ─── RESERVAS ─── */}
        {section === 'reservas' && (
          <>
            <TabRow>
              <Tab $active={reservationFilter === 'all'} onClick={() => setReservationFilter('all')}>
                Todas
              </Tab>
              <Tab $active={reservationFilter === 'pending'} onClick={() => setReservationFilter('pending')}>
                Pendientes
              </Tab>
              <Tab $active={reservationFilter === 'completed'} onClick={() => setReservationFilter('completed')}>
                Completadas
              </Tab>
              <Tab $active={reservationFilter === 'cancelled'} onClick={() => setReservationFilter('cancelled')}>
                Canceladas
              </Tab>
            </TabRow>

            {loading ? (
              <Loading text="Cargando reservas..." />
            ) : reservations.length === 0 ? (
              <EmptyState>
                <h3>No hay reservas en esta vista</h3>
                <p>Cambia el filtro para ver otras.</p>
              </EmptyState>
            ) : (
              <DataTable>
                <table>
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Actividad</th>
                      <th>Empresa</th>
                      <th>Fecha</th>
                      <th>Total</th>
                      <th>Estado</th>
                      <th>Pago</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservations.map(r => (
                      <tr key={r.id}>
                        <td>
                          <strong>{r.customerName}</strong>
                          <div className="sub mono">{r.id.slice(0, 8)}…</div>
                        </td>
                        <td>{r.activityTitle || '—'}</td>
                        <td>{r.empresaName || '—'}</td>
                        <td>
                          {fmtDate(r.date)}
                          <div className="sub">{r.time} · {r.quantity} pax</div>
                        </td>
                        <td>{fmtMoney(r.finalAmount)}</td>
                        <td>
                          <StatusBadge status={r.status} />
                          {r.cancelReason && <div className="sub warn">{r.cancelReason}</div>}
                        </td>
                        <td><StatusBadge status={r.paymentStatus} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DataTable>
            )}
          </>
        )}
      </div>
    </PanelContainer>
  );
};

export default AdminPanel;
